/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type {
	CalendarIssueEventFields,
	CalendarScopeBoard,
	CalendarViewRange,
	CalendarWeekStart,
} from '@jira/calendar__calendar-view/src/controllers/calendar-store/types.tsx';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import { createLocalStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import parameters from '@jira/calendar__calendar-view/src/ui/__generated__/CalendarContentQuery$parameters';
import type {
	JiraViewScopeInput,
	JiraCalendarViewConfigurationInput,
	JiraCalendarIssuesInput,
	JiraCalendarVersionsInput,
	JiraCalendarSprintsInput,
	JiraCalendarMode,
	JiraCalendarWeekStart,
} from '@jira/calendar__calendar-view/src/ui/__generated__/CalendarContentQuery.graphql';
import type { Queries, ExtraProps } from './ExtraProps.types.tsx';

const storageProvider = typeof window !== 'undefined' ? createLocalStorageProvider() : undefined;

type CalendarViewSettings = {
	additionalFilterQuery: string;
	weekStartsOn: CalendarWeekStart;
};

type CalendarBoardViewSettings = {
	additionalFilterQuery: string;
};

const DEFAULT_CALENDAR_BOARD_SETTINGS: CalendarBoardViewSettings = {
	additionalFilterQuery: '',
};

const SETTINGS_STORAGE_KEY = 'calendar:settings:software';
/**
 * Must append boardId to the storage key to avoid conflicts with other boards.
 *
 * @example
 * const boardSpecificKey = `${CALENDAR_BOARD_SETTINGS_STORAGE_KEY}:${boardId}`;
 */
const CALENDAR_BOARD_SETTINGS_STORAGE_KEY = 'calendar:settings:software:board';

function getViewSettings(key: string): CalendarViewSettings | undefined {
	try {
		// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
		const serializedValue = globalThis.localStorage?.getItem(key);

		return serializedValue !== null
			? // oxlint-disable-next-line typescript/consistent-type-assertions
				(JSON.parse(serializedValue) as CalendarViewSettings)
			: undefined;
	} catch {
		return undefined;
	}
}

const getCalendarBoardSettingsFromLocalStorage = (boardId: string): CalendarBoardViewSettings => {
	const boardSpecificKey = `${CALENDAR_BOARD_SETTINGS_STORAGE_KEY}:${boardId}`;
	const settings: Partial<CalendarBoardViewSettings> | undefined =
		storageProvider?.get(boardSpecificKey);

	return {
		additionalFilterQuery:
			settings?.additionalFilterQuery ?? DEFAULT_CALENDAR_BOARD_SETTINGS.additionalFilterQuery,
	};
};

function toBoardAri({ siteId, boardId }: { siteId: string; boardId: string }) {
	return `ari:cloud:jira-software:${siteId}:board/${boardId}`;
}

function toMidnightUTCString(date: Date) {
	const utcMilliseconds = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
	return new Date(utcMilliseconds).toISOString();
}

function toDate(date: string | null | undefined): Date {
	if (!date) {
		return new Date();
	}
	try {
		const _date = new Date(date);
		return new Date(_date.getFullYear(), _date.getMonth(), _date.getDate());
	} catch (e) {
		return new Date();
	}
}

/* Given the current constraints that prevent importing route name constants into the entrypoint,
we are re-defining these constants here in order to perform a route name check.
Consider breaking this entrypoint per route to avoid these types of checks and dependencies. */

const SOFTWARE_CALENDAR: RouteName = 'software-calendar';
const SOFTWARE_CALENDAR_EMBED: RouteName = 'software-calendar-embed';
const SOFTWARE_CALENDAR_CUSTOM_EMBED: RouteName = 'software-calendar-custom-embed';

const TEAM_MANAGED_CALENDAR_ROUTES: string[] = [
	SOFTWARE_CALENDAR,
	SOFTWARE_CALENDAR_EMBED,
	SOFTWARE_CALENDAR_CUSTOM_EMBED,
];

function createScope({ context, tenantContext }: EntryPointRouteParams): CalendarScopeBoard {
	const { cloudId } = tenantContext;

	const isTMP = TEAM_MANAGED_CALENDAR_ROUTES.includes(context.route.name);

	return {
		type: 'board',
		boardId: toBoardAri({
			siteId: cloudId,
			boardId: String(context.match.params.boardId),
		}),
		projectKeys: isTMP ? [String(context.match.params.projectKey)] : [],
	};
}

function buildScopeInput(
	{ tenantContext }: EntryPointRouteParams,
	scope: CalendarScopeBoard,
): JiraViewScopeInput {
	const { cloudId } = tenantContext;

	const scopeInput = {
		ids: [scope.boardId],
	};

	return {
		...scopeInput,
		projectKeys: {
			cloudId,
			keys: scope.projectKeys,
		},
	};
}

function toViewMode(mode: string): JiraCalendarMode {
	switch (mode) {
		case 'week':
			return 'WEEK';
		case 'day':
			return 'DAY';
		default:
			return 'MONTH';
	}
}

function toViewRange(mode: string): CalendarViewRange {
	switch (mode) {
		case 'week':
			return 'week';
		default:
			return 'month';
	}
}

function toWeekStart(weekStart: string): JiraCalendarWeekStart {
	switch (weekStart) {
		case 'monday':
			return 'MONDAY';
		case 'saturday':
			return 'SATURDAY';
		default:
			return 'SUNDAY';
	}
}

function buildConfigurationInput({
	selectedDate,
	viewRange,
	weekStartsOn,
	startDateField,
	endDateField,
}: {
	selectedDate: Date;
	viewRange: CalendarViewRange;
	weekStartsOn: CalendarWeekStart;
	startDateField: string;
	endDateField: string;
}): JiraCalendarViewConfigurationInput {
	return {
		date: toMidnightUTCString(selectedDate),
		mode: toViewMode(viewRange),
		weekStart: toWeekStart(weekStartsOn),
		startDateField,
		endDateField,
		viewId: null,
	};
}

function buildUnscheduledIssuesSearchInput(): JiraCalendarIssuesInput {
	return { additionalFilterQuery: '' };
}

function buildVersionsSearchInput(): JiraCalendarVersionsInput {
	return { versionStatuses: ['RELEASED', 'UNRELEASED'] };
}

function buildSprintsSearchInput(): JiraCalendarSprintsInput {
	return { sprintStates: ['ACTIVE', 'FUTURE', 'CLOSED'] };
}

type CalendarEntryPointComponent = EntryPointComponent<Queries, {}, {}, ExtraProps>;

export const calendarEntryPoint: EntryPoint<CalendarEntryPointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForUserVisible<CalendarEntryPointComponent>(
			/* webpackChunkName: "async-jira-calendar" */ './CalendarEntryPoint.tsx',
			{
				entryFsSsrLiftImportToModule: true,
			},
		),
		getPreloadProps: (entryPointRouteParams: EntryPointRouteParams) => {
			const { context, tenantContext } = entryPointRouteParams;
			const { cloudId } = tenantContext;
			const scope = createScope(entryPointRouteParams);
			const { date, viewRange: viewRangeQueryParam, filter: filterQueryParam } = context.query;
			const selectedDate = toDate(date);
			const boardId = String(context.match.params.boardId);
			const projectKey = String(context.match.params.projectKey);
			const settings = getViewSettings(SETTINGS_STORAGE_KEY);
			const calendarBoardSettings = getCalendarBoardSettingsFromLocalStorage(boardId);
			const issuesSearchInput = {
				additionalFilterQuery: filterQueryParam ?? calendarBoardSettings.additionalFilterQuery,
			};
			const viewRange: CalendarViewRange = toViewRange(viewRangeQueryParam ?? 'month');
			const weekStartsOn = settings?.weekStartsOn ?? 'sunday';
			const startDateField = 'startdate';
			const endDateField = 'duedate';
			const issueEventFields: CalendarIssueEventFields = {
				startDateField,
				endDateField,
			};
			return {
				queries: {
					calendarData: {
						options: {
							fetchPolicy: 'store-and-network' as const,
						},
						parameters,
						variables: {
							cloudId,
							scopeInput: buildScopeInput(entryPointRouteParams, scope),
							configurationInput: buildConfigurationInput({
								selectedDate,
								viewRange,
								weekStartsOn,
								startDateField,
								endDateField,
							}),
							issuesPageSize: 200,
							issuesSearchInput,
							unscheduledIssuesSearchInput: buildUnscheduledIssuesSearchInput(),
							versionsSearchInput: buildVersionsSearchInput(),
							sprintsSearchInput: buildSprintsSearchInput(),
							includeRecurrenceIndicator: true,
							skipSprintSearch: false,
							viewId: null,
							skipVersionsV2Search: false,
							skipUserPreferences: false,
							hasSavedFilterCapability: expValEquals(
								'atlas-96240_saved_views_calendar',
								'enabled',
								true,
								false,
							),

							filterScope: expValEquals('calendar-filter-refinement-sprt', 'isEnabled', true, false)
								? {
										project: {
											projectKey,
										},
									}
								: null,
							isCalendarFilterRefinementEnabled: expValEquals(
								'calendar-filter-refinement-sprt',
								'isEnabled',
								true,
								false,
							),
						},
					},
				},
				entryPoints: {},
				extraProps: {
					scope,
					selectedDate,
					viewRange,
					weekStartsOn,
					issueEventFields,
					boardId,
					hasRecurrenceItemIndicator: true,
					filterScope: expValEquals('calendar-filter-refinement-sprt', 'isEnabled', true, false)
						? {
								project: {
									projectKey,
								},
							}
						: null,
				},
			};
		},
	});
